import { CircularProgress, IconButton } from "@mui/material";
import { Refresh02 } from "@tiny/icons";
import React from "react";

type Callback = () => Promise<void>;

interface RefreshContextValue {
  refresh: () => void;
  registerCallback: (callback: Callback) => () => void;
  refreshing: boolean;
}

interface RefreshProviderProps {
  children?: React.ReactNode;
}

const RefreshContext = React.createContext<RefreshContextValue | undefined>(
  undefined
);

function RefreshProvider(props: RefreshProviderProps) {
  const { children } = props;

  const [refreshing, setRefreshing] = React.useState(false);

  const callbacksRef = React.useRef<Callback[]>([]);

  const refresh = React.useCallback(async () => {
    const promises = callbacksRef.current.map((callback) => callback());
    setRefreshing(true);
    try {
      await Promise.all(promises);
    } finally {
      setRefreshing(false);
    }
  }, []);

  const registerCallback = React.useCallback((callback: Callback) => {
    callbacksRef.current.push(callback);
    return () => {
      callbacksRef.current = callbacksRef.current.filter(
        (item) => item !== callback
      );
    };
  }, []);

  const contextValue = React.useMemo(() => {
    return {
      refresh,
      registerCallback,
      refreshing,
    };
  }, [refresh, registerCallback, refreshing]);

  return (
    <RefreshContext.Provider value={contextValue}>
      {children}
    </RefreshContext.Provider>
  );
}

export function useRefresh() {
  const contextValue = React.useContext(RefreshContext);

  if (!contextValue) {
    throw new Error("The useMessage needs to be used inside a MessageProvider");
  }

  return contextValue;
}

export function useRefreshRegister(callback: Callback) {
  const refreshContext = React.useContext(RefreshContext);

  const callbackRef = React.useRef(callback);

  if (!refreshContext) {
    throw new Error("The useMessage needs to be used inside a MessageProvider");
  }

  const { registerCallback } = refreshContext;

  React.useLayoutEffect(() => {
    callbackRef.current = callback;
    return registerCallback(callbackRef.current);
  }, [callback, registerCallback]);
}

export function RefreshButton() {
  const { refresh, refreshing } = useRefresh();

  return !refreshing ? (
    <IconButton
      onClick={refresh}
      sx={{
        p: 0,
        "&:hover": {
          backgroundColor: "transparent",
        },
      }}
    >
      <Refresh02 sx={{ color: "grey.80" }} />
    </IconButton>
  ) : (
    <CircularProgress size={20} />
  );
}

export default RefreshProvider;
